import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import { yupResolver } from '@hookform/resolvers/yup';
import { TreeNode } from 'primereact/treenode';
import { TreeSelectProps } from 'primereact/treeselect';

import ButtonCustom from '@/components/common/ui/button';
import { CalendarCustomForm } from '@/components/common/ui/calendar';
import { InputTextCustomForm } from '@/components/common/ui/input/Input';
import { useTranslation } from '@/config/i18n';
import useSearch from '@/hooks/useSearch';
import { DATE_FORMAT } from '@/shared/constants';
import { TreeSelectCustomForm } from '@/components/common/ui/treeSelect';
import { PlanFormSearchType, planSearchFormSchema } from '@/shared/schema/plan';
import { planStatus } from '@/shared/constants/plan';
import { parseQueryValue } from '@/lib/utils';

type Props = {
  onSearch: () => void;
}

const PlanFormSearch: React.FC<Props> = ({ onSearch }) => {
  const [t] = useTranslation('');
  const [loading, setLoading] = useState<boolean>();
  const formMethods = useForm<PlanFormSearchType>({
    mode: 'onChange',
    resolver: yupResolver(planSearchFormSchema),
    shouldFocusError: false,
    defaultValues: {
      keyword: '',
      status: 'all',
      start_date: null,
      end_date: null,
    },
  });
  const keywordMsg = t('plan_list.search_plan_placeholder');

  const { handleSearch, queryRaw } = useSearch();
  const { handleSubmit, getValues, setValue, reset, watch } = formMethods;
  const startDate = watch('start_date');
  const endDate = watch('end_date');

  const customValueTemplate = (
    selectedNodes: TreeNode | TreeNode[],
    props: TreeSelectProps,
  ) => {
    if (Array.isArray(selectedNodes) && selectedNodes.length > 0) {
      return (
        <>
          {t('user_list.status_label')}
          {selectedNodes[0].label}
        </>
      );
    }

    const defaultOption = props.options?.find(
      (option) => option.key === props.value,
    );

    return (
      <>
        {t('user_list.status_label')}
        {defaultOption?.label}
      </>
    );
  };

  async function onSubmit() {
    setLoading(true);
    let start_date = getValues('start_date');
    let end_date = getValues('end_date');
    let status = getValues('status');

    start_date = start_date
      ? dayjs(start_date.toString()).format('YYYY-MM-DD')
      : ('' as unknown as any);
    end_date = end_date
      ? dayjs(end_date.toString()).format('YYYY-MM-DD')
      : ('' as unknown as any);
    status = status === 'all' ? '' : status;

    handleSearch({ ...getValues(), start_date, end_date, status });
    setLoading(false);
    onSearch();
  }

  function handleReset() {
    reset();
    onSubmit();
  }

  const getAllFieldNames = () => {
    const values = getValues();
    return Object.keys(values);
  };

  function mapValueFromQuery() {
    getAllFieldNames().forEach((field: any) => {
      const val = queryRaw[field];
      if (val) {
        setValue(field, parseQueryValue(field, val));
      }
    });
  }

  useEffect(() => {
    mapValueFromQuery();
  }, []);

  return (
    <>
      <FormProvider {...formMethods}>
        <form
          className="mt-[16px] flex gap-5"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="w-full search-input relative">
            <span className="pi pi-search absolute top-[14px] left-[14px] text-gray-500 text-[15px] z-40" />
            <InputTextCustomForm
              name="keyword"
              className="!pl-[40px]"
              placeholder={keywordMsg}
            />
          </div>
          <CalendarCustomForm
            className="date-custom"
            placeholder="----/--/--"
            name="start_date"
            dateFormat={DATE_FORMAT}
            wrapClass="min-w-[160px]"
            showIcon
            maxDate={endDate ? new Date(endDate ?? '') : undefined}
            icon={() => <i className="pi pi-calendar text-[18px]" />}
          />
          <div className="flex-full-center">~</div>
          <CalendarCustomForm
            className="date-custom"
            placeholder="----/--/--"
            name="end_date"
            dateFormat={DATE_FORMAT}
            wrapClass="min-w-[160px]"
            showIcon
            minDate={startDate ? new Date(startDate ?? '') : undefined}
            icon={() => <i className="pi pi-calendar text-[18px]" />}
          />
          <TreeSelectCustomForm
            name="status"
            wrapClass="ml-4 min-w-[193px]"
            panelClassName="tree-hide-header tree-disabled-unselect"
            optionsStatic={[
              { key: 'all', label: t('common.status.all') },
              {
                key: planStatus.PUBLIC,
                label: t('common.status_other.active'),
              },
              {
                key: planStatus.DRAFT,
                label: t('common.status_other.inactive'),
              },
            ]}
            valueTemplate={customValueTemplate}
          />
          <ButtonCustom
            type="button"
            severity="secondary"
            onClick={() => handleReset()}
            className="min-w-[60px]"
          >
            <span className="pi pi-refresh" />
          </ButtonCustom>
          <ButtonCustom
            className="min-w-[60px]"
            type="submit"
            disabled={loading}
          >
            <span className="pi pi-search" />
          </ButtonCustom>
        </form>
      </FormProvider>
    </>
  );
};

export default PlanFormSearch;
